import { DataInput, DataInputType } from '@/types/automationFields.ts';
import { IntegrationInputContract } from '@/types/integrations.ts';
import { Box, SelectProps } from '@mantine/core';
import { Badge, Group, Text } from '@mantine/core';
import CustomLoadingOverlay from '@/components/CustomLoadingOverlay.tsx';
import React, { useCallback, useMemo } from 'react';
import useDataInputOptions, {
  getDataInputOptionValue,
  makeNewInputConfig,
} from '@/pages/Editor/components/SharedFields/DataInputSelector/hooks/useDataInputOptions.ts';
import StyledSelect from '@/components/StyledInputFields/StyledSelect.tsx';
import { DEFAULT_DYNAMIC_DATA_INPUT } from '@/utils/constants.ts';
import CustomSelectOption from '@/components/CustomSelectOption.tsx';
import {
  NO_DATA_INPUTS_DESCRIPTION,
  NO_DATA_INPUTS_PLACEHOLDER,
} from '@/pages/Editor/components/SharedFields/DataInputSelector/constants.ts';
import useRetrieveOutputContracts from '@/hooks/api/steps/useRetrieveOutputContracts.ts';
import DataInputSchemaContracts from '@/pages/Editor/components/SharedFields/DataInputSelector/DataInputSchemaContracts.tsx';
import {
  buildOutputContractByOptionValue,
} from '@/pages/Editor/components/SharedFields/DataInputSelector/dataInputSchemaContractUtils.ts';
import { getInputContractStatus } from '@/pages/Editor/components/SharedFields/DataInputSelector/inputContractStatus.ts';

type props = SelectProps & {
  dataInput: DataInput | null;
  stepId: string;
  updateDataInput: (dataInput: DataInput) => void;
  includeEmptyDatasets?: boolean;
  required?: boolean;
  label?: string;
  allowedDataInputTypes?: DataInputType[];
  inputContract?: IntegrationInputContract | null;
  scopeOverride?: 'root' | 'local_subflow';
};

const DataInputSelectorUI = ({
  dataInput,
  stepId,
  updateDataInput,
  includeEmptyDatasets = false,
  required = true,
  label,
  allowedDataInputTypes,
  inputContract,
  scopeOverride,
  description,
  disabled,
  placeholder,
  ...props
}: props) => {
  const {
    options,
    isLoading,
    inventoryError,
    unavailableSourceTypes = [],
  } = useDataInputOptions(stepId, includeEmptyDatasets, scopeOverride);
  const {
    data: outputContracts,
    isError: outputContractsError,
    isPending: outputContractsPending,
  } = useRetrieveOutputContracts(scopeOverride);

  const selectedValue = dataInput ? getDataInputOptionValue(dataInput.config) : null;

  const filteredOptions = useMemo(() => {
    if (!allowedDataInputTypes) {
      return options;
    }
    return options
      .map((group) => ({
        ...group,
        items: group.items.filter((option) =>
          allowedDataInputTypes.some((inputType) => option.value.startsWith(`${inputType} `)),
        ),
      }))
      .filter((group) => group.items.length > 0);
  }, [allowedDataInputTypes, options]);

  const flattenedOptions = useMemo(() => filteredOptions.flatMap((group) => group.items), [filteredOptions]);
  const hasSelectableOptions = flattenedOptions.length > 0;
  const showUnavailableState =
    !isLoading && !hasSelectableOptions && !selectedValue && !!inventoryError;
  const showEmptyState = !inventoryError && !isLoading && !hasSelectableOptions && !selectedValue;
  const selectedSourceUnavailable =
    !!dataInput && unavailableSourceTypes.includes(dataInput.data_input_type);

  const currentOption = useMemo(
    () => flattenedOptions.find((o: any) => o?.value === selectedValue) as any | undefined,
    [flattenedOptions, selectedValue],
  );

  const outputContractByValue = useMemo(() => {
    const contracts = outputContracts?.upstream_output_contracts?.[stepId] ?? [];
    return buildOutputContractByOptionValue(contracts);
  }, [outputContracts?.upstream_output_contracts, stepId]);

  const selectedOutputContract = useMemo(() => {
    if (!selectedValue) {
      return null;
    }

    return outputContractByValue.get(selectedValue) ?? null;
  }, [outputContractByValue, selectedValue]);
  const inputContractStatus = useMemo(
    () =>
      getInputContractStatus(
        inputContract,
        dataInput && selectedValue
          ? [
              {
                sourceType: dataInput.data_input_type,
                outputContract: selectedOutputContract,
                sourceAvailable:
                  Boolean(currentOption) &&
                  (dataInput.data_input_type !== DataInputType.Dynamic ||
                    (!outputContractsPending && !outputContractsError)),
              },
            ]
          : [],
      ),
    [
      currentOption,
      dataInput,
      inputContract,
      outputContractsError,
      outputContractsPending,
      selectedOutputContract,
      selectedValue,
    ],
  );

  const displayOptions = useMemo(() => {
    if (!selectedValue || currentOption || !dataInput) {
      return filteredOptions;
    }

    const lastKnownDisplayName = dataInput.last_known_display_name || dataInput.name;
    return [
      ...filteredOptions,
      {
        group: selectedSourceUnavailable ? 'Unavailable Data Inputs' : 'Deleted Data Inputs',
        items: [
          {
            value: selectedValue,
            label:
              (selectedSourceUnavailable ? 'Data Input Unavailable' : 'Deleted Data Input') +
              (lastKnownDisplayName ? ` (${lastKnownDisplayName})` : ''),
          },
        ],
      },
    ];
  }, [currentOption, dataInput, filteredOptions, selectedValue, selectedSourceUnavailable]);

  const onChange = useCallback(
    (value: string | null) => {
      const next =
        value === null ? DEFAULT_DYNAMIC_DATA_INPUT : makeNewInputConfig(value, flattenedOptions);
      updateDataInput(next);
    },
    [flattenedOptions, updateDataInput],
  );

  return (
    <Box pos="relative">
      <CustomLoadingOverlay visible={isLoading} />
      {inventoryError && (
        <Text size="xs" c="red" role="alert" mb="xs">
          {inventoryError}
        </Text>
      )}
      <StyledSelect
        label={label || 'Data Input'}
        placeholder={
          showUnavailableState
            ? 'Data sources unavailable'
            : showEmptyState
              ? NO_DATA_INPUTS_PLACEHOLDER
              : placeholder || 'Select a data input'
        }
        description={showEmptyState ? description || NO_DATA_INPUTS_DESCRIPTION : description}
        data={displayOptions}
        value={selectedValue}
        onChange={(value) => onChange(value)}
        required={required}
        allowDeselect={false}
        clearable={!required}
        disabled={disabled || showEmptyState || showUnavailableState}
        // Show icon + label in the dropdown
        renderOption={({ option, checked }) => {
          const o = option as any;
          return (
            <CustomSelectOption label={o.label} leftSection={o.leftSection} checked={checked} />
          );
        }}
        leftSection={currentOption?.leftSection ?? null}
        {...props}
      />
      <DataInputSchemaContracts
        outputContracts={selectedOutputContract ? [selectedOutputContract] : []}
      />
      {inputContractStatus && (
        <Group gap="xs" mt="4px" align="flex-start">
          <Badge color={inputContractStatus.color} variant="light" size="sm">
            {inputContractStatus.label}
          </Badge>
          <Text size="11px" c={inputContractStatus.color}>
            {inputContractStatus.detail}
          </Text>
        </Group>
      )}
      {inputContract?.sensitive && (
        <Group gap="xs" mt="4px" align="flex-start">
          <Badge color="violet" variant="light" size="sm">
            Sensitive dataset input
          </Badge>
          <Text size="11px" c="dimmed">
            Dataset values are handled as sensitive and are not shown here.
          </Text>
        </Group>
      )}
    </Box>
  );
};

export default DataInputSelectorUI;
